import React, { useEffect, useMemo, useState } from "react";
import { Button } from "reactstrap";
import { Edit, XCircle } from "react-feather";
import Swal from "sweetalert2";
import api from "../../../api";
import { AesEncrypt, keyPromise } from "../../../Service/crypto";
import {
  ContentRun,
  FormDocument,
  FormElement,
  PAGE_SIZE_PX,
} from "../TemplateCreation/FormDesigner/store/types";

// Output screen for a patient whose template is a published Form Designer
// template: the template's layout is rendered as-is, with the patient's output
// values filled into its bound fields, and an Edit / Save flow that writes back
// through the same endpoints as the field-grid screen.

const normalize = (s: string) => (s || "").toLowerCase().replace(/[^a-z0-9]/g, "");
const isTruthy = (v: string) => /^(y|yes|true|checked|x|1)$/i.test((v || "").trim());
const toText = (v: any) => (typeof v === "string" || typeof v === "number" ? String(v) : "");

const STOP = new Set(["the", "of", "is", "this", "does", "follow", "what", "or", "and", "to", "a", "an", "any", "do", "have", "per"]);
const ALIAS: Record<string, string> = { payor: "payer", ss: "ssn" };
const tokens = (s: string) =>
  (s || "")
    .toLowerCase()
    .replace(/#/g, " number ")
    .replace(/[^a-z0-9]+/g, " ")
    .split(" ")
    .filter(Boolean)
    .map((t) => ALIAS[t] || t)
    .filter((t) => !STOP.has(t));

const similarity = (a: string[], b: string[]) => {
  if (!a.length || !b.length) return 0;
  const bs = new Set(b);
  const inter = new Set(a.filter((t) => bs.has(t))).size;
  return inter / Math.sqrt(new Set(a).size * bs.size);
};
const MIN_MATCH = 0.55;
const answerOf = (v: string): "yes" | "no" | null => {
  const w = (v || "").trim().toLowerCase();
  if (/^(yes|y)\b/.test(w)) return "yes";
  if (/^(no|n)\b/.test(w)) return "no";
  return null;
};
const choiceOf = (label: string): "yes" | "no" | null => {
  const w = (label || "").trim().toLowerCase();
  if (w === "y" || w === "yes") return "yes";
  if (w === "n" || w === "no") return "no";
  return null;
};
const plainText = (el: FormElement) =>
  (el.content || []).every((r) => r.type === "text") ? (el.content || []).map((r: any) => r.value).join("").trim() : "";

let measureCtx: CanvasRenderingContext2D | null = null;
// Real rendered width of a text element, so a value placed after it never lands on top of it
const measureWidth = (el: FormElement, text: string) => {
  try {
    if (!measureCtx) measureCtx = document.createElement("canvas").getContext("2d");
    if (!measureCtx) return 0;
    const st = el.style;
    measureCtx.font = `${st.fontStyle || "normal"} ${st.fontWeight || "normal"} ${typeof st.fontSize === "number" ? st.fontSize : 12}px ${st.fontFamily || "Arial"}`;
    return measureCtx.measureText(text).width;
  } catch {
    return 0;
  }
};

const optionStem = (label: string) => {
  const n = normalize(label);
  return n.slice(0, Math.max(4, n.length - 3));
};
const listHasOption = (value: string, label: string) => !!label && normalize(value).includes(optionStem(label));
const toggleListOption = (value: string, label: string, on: boolean) => {
  const stem = optionStem(label);
  const parts = (value || "").split(",").map((x) => x.trim()).filter(Boolean).filter((x) => !normalize(x).includes(stem));
  if (on) parts.push(label);
  return parts.join(", ");
};

interface Overlay { key: string; x: number; y: number; w: number; h: number; el: FormElement; blank?: boolean }
interface Mapping {
  overlays: Record<number, Overlay[]>;
  choices: Record<string, { key: string; want: "yes" | "no" }>;
  multi: Record<string, string>;
  checkKeys: Record<string, string>;
  used: Set<string>;
  aliases: Record<string, string[]>; // overlay key -> bound keys of hidden template fields it replaces
  hidden: Set<string>; // template elements holding stale sample values that sit where an overlay input is drawn
}

// Plain-text elements right after a label that are baked-in sample values (not another label, option or box)
const isValueText = (o: FormElement) => {
  if (!["label", "text", "paragraph"].includes(o.type)) return false;
  const t = plainText(o);
  return !!t && t.length <= 70 && !/[:?]\s*$/.test(t);
};
const leadingValues = (sameRow: FormElement[]) => {
  const out: FormElement[] = [];
  for (const o of [...sameRow].sort((a, b) => a.x - b.x)) {
    if (!isValueText(o)) break;
    out.push(o);
  }
  return out;
};

// Auto-map unbound label text ("Patient Name:", "Waiting Period?") to the
// closest output field, so a template converted from a document (no explicit
// bindings) still shows each field's answer next to its label.
function buildMapping(layout: FormDocument, data: Record<string, any>): Mapping {
  const overlays: Record<number, Overlay[]> = {};
  const choices: Mapping["choices"] = {};
  const multi: Record<string, string> = {};
  const used = new Set<string>();
  const hidden = new Set<string>();
  const aliases: Record<string, string[]> = {};
  const keys = Object.keys(data).filter((k) => k && (typeof data[k] === "string" || typeof data[k] === "number"));
  const keyTokens = keys.map((k) => tokens(k));

  layout.pages.forEach((page) =>
    page.elements.forEach((el) => {
      if (el.bindings?.dynamicField) used.add(normalize(el.bindings.dynamicField));
      (el.content || []).forEach((r: any) => r.fieldKey && used.add(normalize(r.fieldKey)));
    })
  );

  type Cand = { score: number; pi: number; el: FormElement; ki: number };
  const cands: Cand[] = [];
  layout.pages.forEach((page, pi) =>
    page.elements.forEach((el) => {
      if (!["label", "text", "paragraph"].includes(el.type) || el.visible === false) return;
      const text = plainText(el);
      if (!text || text.length > 70) return;
      const t = tokens(text);
      keys.forEach((k, ki) => {
        const score = similarity(t, keyTokens[ki]);
        if (score >= MIN_MATCH) cands.push({ score, pi, el, ki });
      });
    })
  );
  cands.sort((a, b) => b.score - a.score);

  const takenEl = new Set<string>();
  cands.forEach(({ pi, el, ki }) => {
    const key = keys[ki];
    if (takenEl.has(el.id) || used.has(normalize(key))) return;
    takenEl.add(el.id);
    used.add(normalize(key));
    const page = layout.pages[pi];
    const rowTol = Math.max(el.height * 0.6, 6);
    const textW = Math.max(el.width, measureWidth(el, plainText(el)));
    const sameRow = page.elements.filter(
      (o) => o.id !== el.id && o.visible !== false && Math.abs(o.y - el.y) <= rowTol && o.x >= el.x + textW - 2 && !["hline", "vline", "rect"].includes(o.type)
    );
    // Yes/No question: tick the matching Y/N checkbox on the same row instead of printing text
    const ans = answerOf(toText(data[key]));
    const boxes = sameRow.filter((o) => o.type === "checkbox" && choiceOf(o.typeProps?.checkbox?.label || ""));
    if (ans && boxes.length) {
      boxes.forEach((b) => (choices[b.id] = { key, want: choiceOf(b.typeProps?.checkbox?.label || "")! }));
      return;
    }
    // Option row ("Deductible applies to: [ ] Preventative [ ] Basic [ ] Major"): the checkboxes ARE the
    // field, so tick the ones the value names instead of printing the value in a separate input
    const optionBoxes = sameRow.filter((o) => (o.type === "checkbox" || o.type === "radio") && !choiceOf((o.typeProps?.checkbox || o.typeProps?.radio)?.label || ""));
    if (optionBoxes.length) {
      optionBoxes.forEach((b) => (multi[b.id] = key));
      const named = optionBoxes.some((b) => listHasOption(toText(data[key]), (b.typeProps?.checkbox || b.typeProps?.radio)?.label || ""));
      if (!named) used.delete(normalize(key)); // value names none of the boxes: keep it visible in Additional Fields
      return;
    }
    // The portal value replaces any sample value baked into the template next to this label
    const stale = leadingValues(sameRow);
    const staleIds = new Set(stale.map((o) => o.id));
    const startX = el.x + textW + 4;
    const rest = sameRow.filter((o) => !staleIds.has(o.id));
    const nextX = rest.length ? Math.min(...rest.map((o) => o.x)) : Infinity;
    const pageW = layout.orientation === "landscape" ? PAGE_SIZE_PX[layout.pageSize].height : PAGE_SIZE_PX[layout.pageSize].width;
    const w = Math.min(nextX - startX - 4, pageW - startX - 10, 260);
    if (w < 30) {
      // no room beside the label: leave it to the Additional Fields list rather than overlap
      takenEl.delete(el.id);
      used.delete(normalize(key));
      return;
    }
    stale.forEach((o) => hidden.add(o.id));
    (overlays[pi] = overlays[pi] || []).push({ key, x: startX, y: el.y, w, h: Math.max(el.height, 16), el });
  });
  const pageWidth = layout.orientation === "landscape" ? PAGE_SIZE_PX[layout.pageSize].height : PAGE_SIZE_PX[layout.pageSize].width;
  const checkKeys: Record<string, string> = {};
  layout.pages.forEach((page, pi) => {
    const rowOf = (el: FormElement) => Math.max(el.height * 0.6, 6);
    page.elements.forEach((el) => {
      if (el.visible === false) return;
      // Free checkbox/radio: stored as "<label to its left> - <its own label>" so any option can be saved
      if ((el.type === "checkbox" || el.type === "radio") && !choices[el.id] && !multi[el.id]) {
        const own: string = ((el.type === "checkbox" ? el.typeProps?.checkbox?.label : el.typeProps?.radio?.label) || "").trim();
        const left = page.elements
          .filter((o) => o.id !== el.id && ["label", "text", "paragraph"].includes(o.type) && Math.abs(o.y - el.y) <= rowOf(el) && o.x < el.x && plainText(o))
          .sort((a, b) => b.x - a.x)[0];
        const prefix = left ? plainText(left).replace(/[:?\s]+$/, "") : "";
        checkKeys[el.id] = prefix ? `${prefix} - ${own}` : own;
        return;
      }
      // Blank input after an unmatched "Label:" so every field can be filled in Edit mode
      if (!["label", "text", "paragraph"].includes(el.type) || takenEl.has(el.id)) return;
      const text = plainText(el);
      if (!text || text.length > 70 || !/[:?]\s*$/.test(text)) return;
      const rowTol = rowOf(el);
      const textW = Math.max(el.width, measureWidth(el, text));
      const sameRow = page.elements.filter(
        (o) => o.id !== el.id && o.visible !== false && Math.abs(o.y - el.y) <= rowTol && o.x >= el.x + textW - 2 && !["hline", "vline", "rect"].includes(o.type)
      );
      if (sameRow.some((o) => o.type === "checkbox" || o.type === "radio")) return;
      // No portal value for this label: keep any sample text visible and put the input after it
      const stale = leadingValues(sameRow);
      const staleEnd = stale.length ? Math.max(...stale.map((o) => o.x + Math.max(o.width, measureWidth(o, plainText(o))))) : 0;
      const startX = Math.max(el.x + textW, staleEnd) + 4;
      const rest = sameRow.filter((o) => !stale.some((s) => s.id === o.id));
      const nextX = rest.length ? Math.min(...rest.map((o) => o.x)) : Infinity;
      const w = Math.min(nextX - startX - 4, pageWidth - startX - 10, 260);
      if (w < 50) return;
      (overlays[pi] = overlays[pi] || []).push({ key: text.replace(/[:?\s]+$/, ""), x: startX, y: el.y, w, h: Math.max(el.height, 16), el, blank: true });
    });
  });
  // Whatever static template content still sits under an overlay input (sample values of any element type) is hidden
  Object.entries(overlays).forEach(([pi, list]) =>
    list.forEach((o) =>
      layout.pages[Number(pi)].elements.forEach((e) => {
        if (e.id === o.el.id || e.visible === false || hidden.has(e.id)) return;
        if (["hline", "vline", "rect", "checkbox", "radio", "image", "table", "signature"].includes(e.type)) return;
        const runs: any[] = e.content || [];
        const boundKeys: string[] = [];
        if (e.bindings?.dynamicField) boundKeys.push(e.bindings.dynamicField);
        runs.forEach((r) => r.fieldKey && boundKeys.push(r.fieldKey));
        const t = plainText(e);
        if (/[:?]\s*$/.test(t)) return; // a label, not a value
        // A bound element that also holds fixed text is a label as well as a value: leave it alone
        if (boundKeys.length && runs.some((r) => r.type === "text" && String(r.value || "").trim())) return;
        const ew = Math.max(e.width, t ? measureWidth(e, t) : 0);
        const hOverlap = e.x < o.x + o.w && e.x + ew > o.x;
        const vOverlap = e.y < o.y + o.h && e.y + e.height > o.y;
        if (!(hOverlap && vOverlap)) return;
        hidden.add(e.id);
        // The overlay input stands in for this bound field, so edits must reach its key too
        boundKeys.forEach((bk) => {
          if (normalize(bk) === normalize(o.key)) return;
          const nk = normalize(o.key);
          (aliases[nk] = aliases[nk] || []).push(bk);
        });
      })
    )
  );
  Object.values(overlays).forEach((list) => list.forEach((o) => used.add(normalize(o.key))));
  Object.values(checkKeys).forEach((k) => used.add(normalize(k)));
  return { overlays, choices, multi, checkKeys, used, hidden, aliases };
}

interface Props {
  layout: FormDocument;
  data: Record<string, any>;
  id: string | undefined;
}

const DesignerTemplateView = ({ layout, data, id }: Props) => {
  const [values, setValues] = useState<Record<string, any>>(() => ({ ...data }));
  const [isEditMode, setIsEditMode] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setValues({ ...data });
  }, [data]);

  const keyMap = useMemo(() => {
    const m = new Map<string, string>();
    Object.keys(values).forEach((k) => {
      if (k && !m.has(normalize(k))) m.set(normalize(k), k);
    });
    return m;
  }, [values]);

  const mapping = useMemo(() => buildMapping(layout, data), [layout, data]);
  const extraKeys = useMemo(
    () => Object.keys(data).filter((k) => k && !mapping.used.has(normalize(k)) && (typeof data[k] === "string" || typeof data[k] === "number")),
    [data, mapping]
  );

  const hasKey = (k?: string) => !!k && keyMap.has(normalize(k));
  const getVal = (k?: string) => (k ? toText(values[keyMap.get(normalize(k)) ?? k]) : "");
  const setVal = (k: string, v: string) =>
    setValues((prev) => {
      const next = { ...prev, [keyMap.get(normalize(k)) ?? k]: v };
      (mapping.aliases[normalize(k)] || []).forEach((a) => {
        if (keyMap.has(normalize(a))) next[keyMap.get(normalize(a))!] = v;
      });
      return next;
    });

  const handleCancel = () => {
    setValues({ ...data });
    setIsEditMode(false);
  };

  const handleSave = async () => {
    setSaving(true);
    const filteredEditableData = Object.fromEntries(Object.entries(values).filter(([k]) => k));
    const auditLogData = Object.keys(filteredEditableData).reduce(
      (changed: { field: string; before: any; after: any }[], key) => {
        const before = data[key];
        const after = (filteredEditableData as any)[key];
        if (JSON.stringify(before) !== JSON.stringify(after)) changed.push({ field: key, before, after });
        return changed;
      },
      []
    );
    const Username = localStorage.getItem("UserName") || "";
    const Userid = localStorage.getItem("id") || "";
    const ParentUserId = localStorage.getItem("ParentUserId") || "";
    try {
      const key = await keyPromise;
      const EncParams = await AesEncrypt(key, { id, filteredEditableData });
      const response = await api.fetch("api/update_template_data", { method: "POST", body: JSON.stringify({ EncParams }) });
      const responseData = await response.json();
      if (responseData === "Updated Successfully") {
        if (auditLogData.length > 0) {
          await api.fetch("api/update_template_data_log", {
            method: "POST",
            body: JSON.stringify({ auditData: { auditLogData, Username, Refid: id, Userid, ParentUserId } }),
          });
        }
        Swal.fire({ icon: "success", title: "Insurance Verification Platform", text: "Edited Successfully", customClass: { confirmButton: "btn btn-success" }, allowOutsideClick: false });
        setIsEditMode(false);
      }
    } catch (error) {
      console.error("Error saving:", error);
    } finally {
      setSaving(false);
    }
  };

  const getField = (...keys: string[]) => {
    for (const k of keys) if (hasKey(k)) return getVal(k) || "—";
    return "—";
  };
  const topBarFields = [
    { label: "Patient", value: getField("PatientName", "patient_name") },
    { label: "DOB", value: getField("PatientDob", "dob") },
    { label: "Carrier", value: getField("InsuranceCompanyName", "carrier", "payer", "insurancecompany") },
    { label: "Member ID", value: getField("SsnOrMemberId", "memberid", "member_id") },
  ];

  const baseText = (el: FormElement): React.CSSProperties => {
    const s = el.style;
    return {
      fontFamily: s.fontFamily || "inherit",
      fontSize: typeof s.fontSize === "number" ? `${s.fontSize}px` : s.fontSize || "12px",
      fontWeight: s.fontWeight || "normal",
      fontStyle: s.fontStyle || "normal",
      textDecoration: s.textDecoration || "none",
      color: s.color || "#000000",
      textAlign: s.textAlign || "left",
      lineHeight: s.lineHeight || 1.15,
      letterSpacing: s.letterSpacing || "normal",
    };
  };

  const inputStyle = (el: FormElement, editable: boolean): React.CSSProperties => ({
    width: "100%",
    height: "100%",
    border: "none",
    outline: "none",
    padding: 0,
    margin: 0,
    background: editable ? "#e6f7fa" : "transparent",
    fontSize: el.style.fontSize ? `${el.style.fontSize}px` : "11px",
    fontFamily: el.style.fontFamily || "inherit",
    color: el.style.color || "#000000",
  });

  const renderRuns = (runs: ContentRun[] | undefined) =>
    (runs || []).map((run, i) => {
      if (run.type === "variable") {
        // Bound value (e.g. Yearly Maximum): edit in place when editing
        if (isEditMode && run.fieldKey) {
          const fk = run.fieldKey;
          const txt = getVal(fk);
          return (
            <input
              key={i}
              type="text"
              title={fk}
              value={txt}
              onChange={(e) => setVal(fk, e.target.value)}
              style={{ width: `${Math.max(txt.length + 2, 8)}ch`, maxWidth: "100%", border: "1px solid #7ecbcb", borderRadius: 4, padding: "0 4px", fontSize: "inherit", fontWeight: "inherit", background: "#e6f7fa", color: "#0b4f9e" }}
            />
          );
        }
        return <React.Fragment key={i}>{getVal(run.fieldKey)}</React.Fragment>;
      }
      if (run.type === "textbox" || run.type === "textarea") {
        const editable = isEditMode && !!run.fieldKey;
        return (
          <input
            key={i}
            type="text"
            value={run.fieldKey ? getVal(run.fieldKey) : run.value || ""}
            readOnly={!editable}
            onChange={(e) => run.fieldKey && setVal(run.fieldKey, e.target.value)}
            style={{ width: "100%", border: "1px solid #c0c0c0", borderRadius: 3, padding: "2px 4px", fontSize: 11, background: editable ? "#e6f7fa" : "#fff" }}
          />
        );
      }
      if (run.type === "checkbox" || run.type === "radio") {
        const checked = run.fieldKey ? isTruthy(getVal(run.fieldKey)) : !!run.checked;
        return (
          <label key={i} style={{ display: "inline-flex", alignItems: "center", gap: "0.15em", margin: 0, fontSize: 11 }}>
            <input
              type={run.type}
              checked={checked}
              disabled={!(isEditMode && run.fieldKey)}
              onChange={() => run.fieldKey && setVal(run.fieldKey, checked ? "No" : "Yes")}
              style={{ width: "0.75em", height: "0.75em", margin: 0 }}
            />
            {run.value || ""}
          </label>
        );
      }
      return <React.Fragment key={i}>{run.value}</React.Fragment>;
    });

  const renderBody = (el: FormElement): React.ReactNode => {
    const s = el.style;
    switch (el.type) {
      case "heading":
      case "label":
      case "text":
      case "pagenumber":
        return (
          <div style={{ ...baseText(el), width: "100%", height: "100%", whiteSpace: "nowrap", display: "flex", alignItems: "flex-start" }}>
            {renderRuns(el.content)}
          </div>
        );
      case "paragraph":
        return (
          <div style={{ ...baseText(el), width: "100%", height: "100%", whiteSpace: "pre-wrap", wordBreak: "break-word", display: "flex", alignItems: "flex-start" }}>
            {renderRuns(el.content)}
          </div>
        );
      case "hline":
        return <div style={{ width: "100%", height: 0, borderTop: `${s.borderWidth || 1}px ${s.borderStyle || "solid"} ${s.borderColor || "#000"}` }} />;
      case "vline":
        return <div style={{ width: 0, height: "100%", borderLeft: `${s.borderWidth || 1}px ${s.borderStyle || "solid"} ${s.borderColor || "#000"}` }} />;
      case "checkbox":
      case "radio": {
        const props: any = el.type === "checkbox" ? el.typeProps?.checkbox : el.typeProps?.radio;
        const label = props?.label || "";
        const bound = hasKey(label);
        const choice = mapping.choices[el.id];
        const multiKey = !choice ? mapping.multi[el.id] : undefined;
        const freeKey = !choice && !multiKey && !bound ? mapping.checkKeys[el.id] : undefined;
        const freeSet = !!freeKey && hasKey(freeKey);
        const checked = choice
          ? answerOf(getVal(choice.key)) === choice.want
          : multiKey
          ? listHasOption(getVal(multiKey), label)
          : bound
          ? isTruthy(getVal(label))
          : freeSet
          ? isTruthy(getVal(freeKey))
          : !!props?.checked;
        const toggle = !isEditMode
          ? undefined
          : choice
          ? () => setVal(choice.key, choice.want === "yes" ? "Yes" : "No")
          : multiKey
          ? () => setVal(multiKey, toggleListOption(getVal(multiKey), label, !checked))
          : bound
          ? () => setVal(label, checked ? "No" : "Yes")
          : freeKey
          ? () => setVal(freeKey, checked ? "No" : "Yes")
          : undefined;
        return (
          <label style={{ ...baseText(el), display: "flex", alignItems: "center", gap: "0.15em", height: "100%", margin: 0, whiteSpace: "nowrap" }}>
            <input type={el.type} checked={checked} disabled={!toggle} onChange={toggle} style={{ width: "0.75em", height: "0.75em", margin: 0, flexShrink: 0 }} />
            {label}
          </label>
        );
      }
      case "dropdown":
      case "textbox":
      case "textarea":
      case "date": {
        const field = el.bindings?.dynamicField;
        const editable = isEditMode && !!field;
        const value = field ? getVal(field) : el.bindings?.defaultValue ?? "";
        const box: React.CSSProperties = {
          width: "100%",
          height: "100%",
          boxSizing: "border-box",
          display: "flex",
          alignItems: "center",
          padding: "0 3px",
          background: s.background && s.background !== "transparent" ? s.background : "#ffffff",
          border: `${s.borderWidth || 1}px ${s.borderStyle || "solid"} ${s.borderColor || "#cbd5e1"}`,
          borderRadius: s.borderRadius || 3,
        };
        return (
          <div style={box}>
            <input
              type="text"
              value={value}
              readOnly={!editable}
              onChange={(e) => field && setVal(field, e.target.value)}
              style={inputStyle(el, editable)}
            />
          </div>
        );
      }
      case "signature":
        return <div style={{ ...baseText(el), width: "100%", height: "100%", borderBottom: "1px solid #000", display: "flex", alignItems: "flex-end", color: "#999" }}>{el.typeProps?.signature?.placeholderText || "Signature"}</div>;
      case "image": {
        const src = el.typeProps?.image?.src;
        return src ? <img src={src} alt="" style={{ width: "100%", height: "100%", objectFit: el.typeProps?.image?.fit || "contain" }} /> : null;
      }
      case "table": {
        const t = el.typeProps?.table;
        if (!t) return null;
        const covered = new Set<string>();
        t.merges.forEach((m) => {
          for (let r = m.r; r < m.r + m.rowSpan; r++)
            for (let c = m.c; c < m.c + m.colSpan; c++) if (!(r === m.r && c === m.c)) covered.add(`${r}_${c}`);
        });
        return (
          <table style={{ width: "100%", height: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
            <tbody>
              {Array.from({ length: t.rows }, (_, r) => (
                <tr key={r} style={{ height: t.rowHeights[r] || "auto" }}>
                  {Array.from({ length: t.cols }, (_, c) => {
                    if (covered.has(`${r}_${c}`)) return null;
                    const merge = t.merges.find((m) => m.r === r && m.c === c);
                    return (
                      <td key={c} rowSpan={merge?.rowSpan || 1} colSpan={merge?.colSpan || 1} style={{ border: "1px solid #999", padding: 4, width: `${t.colWidths[c] || 100 / t.cols}%`, verticalAlign: "middle", fontSize: 12 }}>
                        {renderRuns(t.cells?.[r]?.[c])}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        );
      }
      default:
        return null; // rect and anything else: styled wrapper only
    }
  };

  const base = PAGE_SIZE_PX[layout.pageSize] || PAGE_SIZE_PX.Letter;
  const pageWidth = layout.orientation === "landscape" ? base.height : base.width;
  const pageHeight = layout.orientation === "landscape" ? base.width : base.height;

  return (
    <div style={{ border: "1px solid #e0e0e0", borderRadius: 10, overflow: "hidden", background: "#fff", boxShadow: "0 2px 12px rgba(0,0,0,0.08)" }}>
      <div style={{ background: "#f8f9fa", borderBottom: "1px solid #e0e0e0", padding: "14px 24px", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
        <div style={{ display: "flex", gap: 28, flexWrap: "wrap" }}>
          {topBarFields.map((f) => (
            <div key={f.label}>
              <div style={{ fontSize: 10, color: "#888", fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.6 }}>{f.label}</div>
              <div style={{ fontSize: 13, fontWeight: 600, color: "#222", marginTop: 2 }}>{f.value}</div>
            </div>
          ))}
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          {isEditMode ? (
            <>
              <Button color="danger" size="sm" onClick={handleCancel} disabled={saving} style={{ display: "flex", alignItems: "center", gap: 4 }}>
                <XCircle size={14} /> Cancel
              </Button>
              <Button color="success" size="sm" onClick={handleSave} disabled={saving}>
                {saving ? "Saving..." : "Save"}
              </Button>
            </>
          ) : (
            <Button color="primary" size="sm" onClick={() => setIsEditMode(true)} style={{ display: "flex", alignItems: "center", gap: 6, backgroundColor: "#1abc9c", border: "none" }}>
              Edit <Edit size={13} />
            </Button>
          )}
        </div>
      </div>

      <div style={{ background: "#e9edf0", padding: 20, overflow: "auto", maxHeight: "75vh" }}>
        {layout.pages.map((page, i) => (
          <div
            key={page.id || i}
            style={{ position: "relative", width: pageWidth, height: pageHeight, background: "#fff", margin: i === 0 ? "0 auto" : "20px auto 0", boxShadow: "0 1px 6px rgba(0,0,0,0.15)", overflow: "hidden", flexShrink: 0 }}
          >
            {page.elements
              .filter((el) => el.visible !== false && !mapping.hidden.has(el.id))
              .map((el) => {
                const s = el.style;
                const isInput = el.type === "textbox" || el.type === "textarea" || el.type === "date" || el.type === "dropdown";
                return (
                  <div
                    key={el.id}
                    style={{
                      position: "absolute",
                      left: el.x,
                      top: el.y,
                      width: el.width,
                      height: el.height,
                      transform: el.rotation ? `rotate(${el.rotation}deg)` : undefined,
                      background: s.background && s.background !== "transparent" ? s.background : undefined,
                      borderWidth: isInput ? 0 : s.borderWidth || 0,
                      borderStyle: isInput ? undefined : s.borderStyle && s.borderStyle !== "none" ? s.borderStyle : undefined,
                      borderColor: isInput ? undefined : s.borderColor,
                      borderRadius: s.borderRadius,
                      padding: s.padding ?? 0,
                      boxSizing: "border-box",
                      overflow: ["text", "label", "heading", "rect", "checkbox"].includes(el.type) ? "visible" : "hidden",
                      zIndex: isInput || el.type === "checkbox" || el.type === "radio" ? 10 : el.type === "rect" ? 1 : 2,
                      pointerEvents: el.type === "rect" || el.type === "hline" || el.type === "vline" ? "none" : "auto",
                    }}
                  >
                    {renderBody(el)}
                  </div>
                );
              })}
            {(mapping.overlays[i] || []).filter((o) => !o.blank || isEditMode || getVal(o.key)).map((o) => (
              <div key={`ov-${o.el.id}`} style={{ position: "absolute", left: o.x, top: o.y, width: o.w, height: o.h, zIndex: 11, display: "flex", alignItems: "flex-start" }}>
                <input
                  type="text"
                  title={o.key}
                  value={getVal(o.key)}
                  readOnly={!isEditMode}
                  onChange={(e) => setVal(o.key, e.target.value)}
                  style={{
                    width: "100%",
                    boxSizing: "border-box",
                    outline: "none",
                    // Edit mode: bordered, tinted input (same look as the field-grid screen); read mode: plain text
                    border: isEditMode ? "1px solid #7ecbcb" : "none",
                    borderRadius: isEditMode ? 6 : 0,
                    padding: isEditMode ? "0 8px" : 0,
                    height: isEditMode ? o.h + 6 : undefined,
                    marginTop: isEditMode ? -3 : 0,
                    cursor: isEditMode ? "text" : "default",
                    background: isEditMode ? "#e6f7fa" : "transparent",
                    fontFamily: o.el.style.fontFamily || "inherit",
                    fontSize: typeof o.el.style.fontSize === "number" ? `${o.el.style.fontSize}px` : "12px",
                    fontWeight: 600,
                    color: "#0b4f9e",
                    lineHeight: 1.15,
                  }}
                />
              </div>
            ))}
          </div>
        ))}
      </div>

      {extraKeys.length > 0 && (
        <div style={{ padding: "20px 24px", borderTop: "1px solid #e0e0e0" }}>
          <h6 style={{ fontWeight: 700, marginBottom: 16, color: "#222", fontSize: 16 }}>Additional Fields</h6>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "16px 20px" }}>
            {extraKeys.map((k) => (
              <div key={k} style={{ display: "flex", flexDirection: "column" }}>
                <label style={{ fontSize: 10, fontWeight: 700, color: "#999", textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 5 }}>{k}</label>
                <input
                  type="text"
                  value={getVal(k)}
                  readOnly={!isEditMode}
                  onChange={(e) => setVal(k, e.target.value)}
                  style={{ background: isEditMode ? "#e6f7fa" : "#f5f8fa", border: isEditMode ? "1px solid #7ecbcb" : "1px solid #e8e8e8", borderRadius: 6, padding: "9px 12px", fontSize: 14, color: "#333", outline: "none" }}
                />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default DesignerTemplateView;
